import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import Loading from "../../Components/Loading/Loading";
import Translate, {
  useTranslateText,
} from "../../Components/Multilanguage/Translate";
import {
  getVideoSubscription,
  checkIfEmailExists,
} from "../Subscription/service";
import { login } from "../Accounts/Login/service";
import { registerUser } from "../Accounts/Register/service";
import { authenticate2, analytics2, authenticate } from "../../network/service";
import { getAccessToken } from "../../Redux/AToken/ATokenSlice";
import { getUser } from "../../Redux/UserSlice/UserSlice";
import { getUserDetails } from "../../Redux/UserDetailsSlice/UserDetailsSlice";
import { getLanguageModal } from "../../Redux/LanguageModal/languageModalSlice";
import { generateGiftSession } from "./service";

const GiftAdd = () => {
  const [authSectionExpanded, setAuthSectionExpanded] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [authMode, setAuthMode] = useState("create");
  const [howGiftsExpanded, setHowGiftsExpanded] = useState(false);
  const [values, setValues] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translateText = useTranslateText();

  const accessToken = useSelector((state) => state.accessToken?.value);
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const user = useSelector((state) => state?.user?.value);
  const userDetails = useSelector((state) => state?.userDetails?.value);

  const userEmail = user?.user_email || userDetails?.user_email;
  const logo = projectInfo?.projectConfig?.config?.LOGO;

  const appInfo = {
    accessToken: accessToken,
    projectDetails: projectInfo,
  };

  const subscriptionOptions = subscriptions.map((sub) => ({
    value: sub.subscription_id,
    label: `${sub.subscription_name} • ${sub.subscription_text}`,
    subscription: sub,
  }));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getDurationOptions = (subscription) => {

    if (!subscription) return [];

    const subscriptionText = (
      subscription.subscription_text || ""
    ).toLowerCase();
    let hasMonth = false;
    let hasYear = false;
    if (subscription.subscription_type_id == 3) {
      hasMonth = true;
    }
    if (subscription.subscription_type_id == 4) {
      hasYear = true;
    }
    if (hasMonth) {
      return [
        {
          value: 1,
          label: <Translate textKey={"one_month"} />,
          duration: 1,
          type: "month",
        },
        {
          value: 3,
          label: <Translate textKey={"three_months"} />,
          duration: 3,
          type: "month",
        },
        {
          value: 6,
          label: <Translate textKey={"six_months"} />,
          duration: 6,
          type: "month",
        },
      ];
    } else if (hasYear) {
      return [
        {
          value: 1,
          label: <Translate textKey={"one_year"} />,
          duration: 1,
          type: "year",
        },
        {
          value: 2,
          label: <Translate textKey={"two_years"} />,
          duration: 2,
          type: "year",
        },
      ];
    } else {
      return [
        {
          value: 1,
          label: <Translate textKey={"one_month"} />,
          duration: 1,
          type: "month",
        },
        {
          value: 3,
          label: <Translate textKey={"three_months"} />,
          duration: 3,
          type: "month",
        },
        {
          value: 6,
          label: <Translate textKey={"six_months"} />,
          duration: 6,
          type: "month",
        },
      ];
    }
  };

  const durationOptions = selectedSubscription
    ? getDurationOptions(selectedSubscription.subscription)
    : [];

  const renderDescription = (desc) => {
    if (!desc && desc !== 0) return null;
    const normalized = String(desc).replace(/\\n/g, "\n");
    const parts = normalized.split(/\r\n|\r|\n/);
    return parts.map((part, idx) => (
      <React.Fragment key={idx}>
        {part}
        {idx < parts.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const calculateTotalPrice = () => {
    if (!selectedSubscription || !selectedDuration) return 0;
    const basePrice = selectedSubscription.subscription.price || 0;
    return (basePrice * selectedDuration.duration).toFixed(2);
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (subscriptions.length > 0 && !selectedSubscription) {
      const firstSubscription = {
        value: subscriptions[0].subscription_id,
        label: `${subscriptions[0].subscription_name} • ${subscriptions[0].subscription_text}`,
        subscription: subscriptions[0],
      };
      setSelectedSubscription(firstSubscription);
    }
  }, [subscriptions, selectedSubscription]);

  useEffect(() => {
    if (selectedSubscription) {
      const durations = getDurationOptions(selectedSubscription.subscription);
      if (durations.length > 0) {
        setSelectedDuration(durations[0]);
      }
    }
  }, [selectedSubscription]);

  useEffect(() => {
    if (user) {
      setAuthSectionExpanded(false);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleEmailValidation = async () => {
    if (authMode !== "create") {
      return false;
    } else {
      const isEmailValid = await validateEmail(values.email);
      if (isEmailValid) {
        setErrors({
          ["email"]: "",
          ["password"]: "",
        });
        values.email && isEmailAlreadyRegistered();
      } else {
        setErrors({
          ["email"]: <Translate textKey={"invalid_email_id"} />,
          ["password"]: "",
        });
      }
    }
  };

  const validateEmail = async (email) => {
    const regex = /^[^@]+@[^@]+\.[^@]+$/;
    return regex.test(email);
  };

  const isEmailAlreadyRegistered = async () => {
    const response = await checkIfEmailExists(appInfo, {
      user_email: values.email,
    });

    if (response?.status == 201) {
      setAuthMode("signin");
    } else {
      //   console.log(response?.data?.message)
    }
  };

  const validateForm = async () => {
    const isEmailValid = await validateEmail(values.email);
    const isPasswordValid = await validatePassword(values.password);

    if (isEmailValid && isPasswordValid) {
      setErrors({});
      return true;
    } else {
      manageErrorMessage(isEmailValid, isPasswordValid);
      return false;
    }
  };

  const validatePassword = async (password) => {
    if (password && password != "") {
      return true;
    } else {
      setErrors({
        ...errors,
        ["password"]: <Translate textKey={"password_required"} />,
      });
      return false;
    }
  };

  const manageErrorMessage = (isEmailValid, isPasswordValid) => {
    const passwordMsg = isPasswordValid ? (
      ""
    ) : (
      <Translate textKey={"password_required"} />
    );
    const emailMsg = isEmailValid ? (
      ""
    ) : (
      <Translate textKey={"email_required_message"} />
    );
    setErrors({
      password: passwordMsg,
      email: emailMsg,
    });
  };

  const updateDeviceAnalytics = async (
    userId,
    appInfo,
    userEmail,
    userName
  ) => {
    await analytics2(userId, appInfo, userEmail, userName);
  };

  const tokenAuthenticate = async () => {
    const response = await authenticate(appInfo);

    localStorage.setItem(
      "selectedLanguageCode",
      response?.data?.user_language?.short_code
        ? response?.data?.user_language?.short_code
        : response?.user_language?.short_code
    );
    localStorage.setItem(
      "selectedLanguageId",
      response?.data?.user_language?.language_id
        ? response?.data?.user_language?.language_id
        : response?.user_language?.language_id
    );

    dispatch(
      getLanguageModal({
        languageModal: {
          selectedLanguageCode: response?.data?.user_language?.short_code
            ? response?.data?.user_language?.short_code
            : response?.user_language?.short_code,
        },
      })
    );

    dispatch(
      getAccessToken({
        accessToken: response?.data?.token
          ? response?.data?.token
          : response?.token,
      })
    );

    dispatch(
      getUserDetails({
        userDetails: response?.data ? response?.data : response,
      })
    );
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await getVideoSubscription(appInfo, null, null, "list", true);

      if (response.status === 200 && response.data?.data) {
        setSubscriptions(response.data.data);
      } else {
        toast.error(
          <Translate textKey={"failed_to_load_subscription_plans"} />
        );
      }
    } catch (error) {
      toast.error(<Translate textKey={"failed_to_load_subscription_plans"} />);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();

    if (authMode === "create") {
      if (!(await validateForm())) return;
    } else {
      if (!(await validateForm())) return;
    }

    try {
      setLoading(true);

      let response;
      if (authMode === "create") {
        response = await registerUser(appInfo, {
          email: values.email,
          password: values.password,
        });
      } else {
        response = await login(
          { email: values.email, password: values.password },
          appInfo
        );
      }

      if (response.status === 200 && response.data) {
        localStorage.setItem("userId", response.data.data[0]?.user_id);
        tokenAuthenticate();
        updateDeviceAnalytics(
          response.data.data[0]?.user_id,
          appInfo,
          response.data.data[0]?.user_email,
          response.data.data[0]?.first_name
        );
        setTimeout(() => {
          dispatch(
            getUser({
              user: response.data.data[0]?.user_id,
            })
          );
        }, 1000);

        toast.success(
          authMode === "create" ? (
            <Translate textKey={"account_created_successfully"} />
          ) : (
            <Translate textKey={"login_successful"} />
          )
        );
        setAuthSectionExpanded(false);
      } else if (response?.status == 201) {
        toast.info(response?.data?.message);
      } else {
        toast.error(response.data?.message);
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    try {
      setLoading(true);

      const purchaseData = {
        period: selectedDuration.duration,
        sub_id: selectedSubscription.subscription.subscription_id,
      };

      const response = await generateGiftSession(appInfo, purchaseData);

      if (response?.status === 200 && response?.data?.success) {
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      //   console.error("Purchase error:", error);
      toast.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'var(--bg-color)',
      color: "var(--text-color)",
      borderColor: state.isFocused
        ? "var(--button-color)"
        : "var(--border-color)",
      borderRadius: "8px",
      padding: "8px",
      boxShadow: state.isFocused ? "0 0 0 1px var(--button-color)" : "none",
      "&:hover": { borderColor: "var(--button-color)" },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      zIndex: 9999,
      backgroundColor: "var(--bg-color)",
      color: "var(--text-color)",
      border: "1px solid var(--border-color)",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "var(--button-color)"
        : state.isFocused
          ? "var(--bg-color)"
          : "var(--bg-color)",
      color: state.isSelected
        ? "var(--button-text-color)"
        : "var(--text-color)",
      "&:hover": {
        backgroundColor: state.isSelected
          ? "var(--button-color)"
          : "var(--bg-color)",
      },
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <ToastContainer />
      <div className="giftAddPage">
        <div className={`giftContainer ${!user ? 'no-user' : ''}`}>
          <div className={`giftLeft ${!user ? 'no-user' : ''}`}>
            <div className="giftContent">
              <div className="giftAuthSection">
                <div
                  className={`accountSection ${user ? "completed" : authSectionExpanded ? "expanded" : ""
                    }`}
                >
                  <div
                    className="sectionHeader"
                    onClick={() =>
                      !user && setAuthSectionExpanded(!authSectionExpanded)
                    }
                  >
                    <div className="sectionNumber">1</div>
                    <h3>
                      <Translate textKey="account_details" />
                    </h3>
                  </div>

                  {user ? (
                    <div className="accountInfo">
                      <div className="userEmail">{userEmail}</div>
                    </div>
                  ) : (
                    authSectionExpanded && (
                      <div className="authFormContainer">
                        <div className="authModeToggle">
                          <span
                            className={authMode === "create" ? "active" : ""}
                            onClick={() => setAuthMode("create")}
                          >
                            <Translate textKey="create_account" />
                          </span>
                          <span
                            className={authMode === "signin" ? "active" : ""}
                            onClick={() => setAuthMode("signin")}
                          >
                            <Translate textKey="sign_in" />
                          </span>
                        </div>

                        <form onSubmit={handleAuth} className="giftAuthForm">
                          <div className="formGroup">
                            <label htmlFor="email">
                              <Translate textKey="email" />
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={values.email}
                              onChange={handleInputChange}
                              onFocus={() => {
                                setErrors({ ...errors, ["email"]: "" });
                              }}
                              placeholder={translateText("email")}
                              className={errors.email ? "error" : ""}
                            />
                            {errors.email && (
                              <span className="error-text">{errors.email}</span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="password">
                              <Translate textKey="password" />
                            </label>
                            <input
                              type="password"
                              id="password"
                              name="password"
                              value={values.password}
                              onChange={handleInputChange}
                              onFocus={() => {
                                handleEmailValidation();
                                setErrors({ ...errors, ["password"]: "" });
                              }}
                              placeholder={translateText("password")}
                              className={errors.password ? "error" : ""}
                            />
                            {errors.password && (
                              <span className="error-text">
                                {errors.password}
                              </span>
                            )}
                          </div>

                          <button type="submit" className="continue-btn">
                            <Translate textKey="continue" />
                          </button>

                          <div className="auth-switch-text">
                            {authMode === "create" ? (
                              <>
                                <Translate textKey="register_sign_in_text" />{" "}
                                <span
                                  onClick={() => setAuthMode("signin")}
                                  className="switch-link"
                                >
                                  <Translate textKey="sign_in" />
                                </span>
                              </>
                            ) : (
                              <>
                                <Translate textKey="sign_in_register_text" />{" "}
                                <span
                                  onClick={() => setAuthMode("create")}
                                  className="switch-link"
                                >
                                  <Translate textKey="sign_up" />
                                </span>
                              </>
                            )}
                          </div>
                        </form>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="gift-details-section">
                <div className="section-header">
                  <div className="section-number">2</div>
                  <h3>
                    <Translate textKey="gift_details" />
                  </h3>
                </div>

                {user && (
                  <div className="gift-form-content">
                    <div className="form-group">
                      <label htmlFor="subscription-select">
                        <Translate textKey="select_gift" />
                      </label>
                      <Select
                        id="subscription-select"
                        options={subscriptionOptions}
                        value={selectedSubscription}
                        onChange={setSelectedSubscription}
                        placeholder={translateText("select_subscription")}
                        isSearchable
                        styles={customSelectStyles}
                        className="subscriptionDropdown"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>

                    {selectedSubscription && (
                      <div className="form-group">
                        <label>
                          <Translate textKey="select_gift_duration" />
                        </label>
                        <div className="durationOptions">
                          {durationOptions.map((option) => (
                            <div
                              key={option.value}
                              className={`durationOption ${selectedDuration?.value === option.value
                                ? "selected"
                                : ""
                                }`}
                              onClick={() => setSelectedDuration(option)}
                            >
                              <div className="durationCircle">
                                {selectedDuration?.value === option.value && (
                                  <div className="durationCircleInner" />
                                )}
                              </div>
                              <span className="durationLabel">
                                {option.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedSubscription && selectedDuration && (
                      <>
                        <div className="priceSummary">
                          <div className="totalPrice">
                            <span className="totalLabel">
                              <Translate textKey="total" />:{" "}
                            </span>
                            <span className="totalAmount">
                              ${calculateTotalPrice()}
                            </span>
                          </div>
                        </div>

                        <button
                          className="continue-btn"
                          onClick={handlePurchase}
                          disabled={
                            loading ||
                            !user ||
                            !selectedSubscription ||
                            !selectedDuration
                          }
                        >
                          <Translate textKey="continue" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`giftRight ${!user ? 'no-user' : ''}`}>
            <div className="giftSidebar">
              <div className="giftCardContainer">
                <div className="giftCardInner">
                  <div className="giftCardLogo">
                    <img src={logo} alt="Logo" className="logo" />
                  </div>

                  <div className="giftCardContent">
                    <h3 className="giftCardTitle">
                      {selectedSubscription?.subscription ? (
                        selectedSubscription.subscription.subscription_name
                      ) : (
                        <Translate textKey="gift" />
                      )}
                    </h3>
                    <p className="giftCardSubtitle">
                      {selectedSubscription?.subscription &&
                        selectedDuration ? (
                        <>
                          {`${selectedDuration.duration} ${selectedDuration.type
                            }${selectedDuration.duration > 1 ? "s" : ""}`}{" "}
                          <Translate textKey="premium_content_duration" />
                        </>
                      ) : (
                        <Translate textKey="access_to_premium_content" />
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <hr className="centered-hr" />

              {!user ? (
                <div className="howGiftsWork">
                  <h3 className="sectionTitle">
                    <Translate textKey="how_gifts_work" />
                  </h3>

                  <div className="giftStep">
                    <div className="stepIcon stepIcon1">💰</div>
                    <div className="stepContent">
                      <h4>
                        <Translate textKey="buy_a_gift" />
                      </h4>
                      <p>
                        <Translate textKey="pick_gift_to_share" />
                      </p>
                    </div>
                  </div>

                  <div className="giftStep">
                    <div className="stepIcon stepIcon2">📧</div>
                    <div className="stepContent">
                      <h4>
                        <Translate textKey="share_details" />
                      </h4>
                      <p>
                        <Translate textKey="send_gift_details" />
                      </p>
                    </div>
                  </div>

                  <div className="giftStep">
                    <div className="stepIcon stepIcon3">🎁</div>
                    <div className="stepContent">
                      <h4>
                        <Translate textKey="recipient_takes_over" />
                      </h4>
                      <p>
                        <Translate textKey="they_use_code_to_unlock" />
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {selectedSubscription?.subscription && selectedDuration && (
                    <div className="membershipSection">
                      <h4 className="membershipTitle">
                        <Translate textKey="membership" />
                      </h4>
                      <h3 className="membershipName">
                        {selectedSubscription.subscription.subscription_name}
                      </h3>
                      <p className="membershipDescription">
                        {renderDescription(
                          selectedSubscription.subscription.description
                        )}
                      </p>

                      <div className="totalSection">
                        <div className="totalRow">
                          <span className="totalLabel">
                            <Translate textKey="total" />
                          </span>
                          <span className="totalPrice">
                            USD ${calculateTotalPrice()}
                          </span>
                        </div>
                        <div className="secureCheckout">
                          🔒 <Translate textKey="secure_checkout" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* <div className="howGiftsWorkExpandable">
                    <div
                      className="expandableHeader"
                      onClick={() => setHowGiftsExpanded(!howGiftsExpanded)}
                    >
                      <h3 className="sectionTitle">
                        <Translate textKey="how_gifts_work" />
                      </h3>
                      <span className="expandIcon">
                        {howGiftsExpanded ? "▲" : "▼"}
                      </span>
                    </div>

                    {howGiftsExpanded && (
                      <div className="expandableContent">
                        <div className="giftStep">
                          <div className="stepIcon stepIcon1">💰</div>
                          <div className="stepContent">
                            <h4>
                              <Translate textKey="buy_a_gift" />
                            </h4>
                            <p>
                              <Translate textKey="pick_gift_to_share" />
                            </p>
                          </div>
                        </div>

                        <div className="giftStep">
                          <div className="stepIcon stepIcon2">📧</div>
                          <div className="stepContent">
                            <h4>
                              <Translate textKey="share_details" />
                            </h4>
                            <p>
                              <Translate textKey="send_gift_details" />
                            </p>
                          </div>
                        </div>

                        <div className="giftStep">
                          <div className="stepIcon stepIcon3">🎁</div>
                          <div className="stepContent">
                            <h4>
                              <Translate textKey="recipient_takes_over" />
                            </h4>
                            <p>
                              <Translate textKey="they_use_code_to_unlock" />
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div> */}
                </>
              )}
            </div>
          </div>
          {user && (
                    <div className="howGiftsWork outside">
                      <div className="border">
                      <h3 className="sectionTitle">
                        <Translate textKey="how_gifts_work" />
                      </h3>

                      <div className="giftStep">
                        <div className="stepIcon stepIcon1">💰</div>
                        <div className="stepContent">
                          <h4>
                            <Translate textKey="buy_a_gift" />
                          </h4>
                          <p>
                            <Translate textKey="pick_gift_to_share" />
                          </p>
                        </div>
                      </div>

                      <div className="giftStep">
                        <div className="stepIcon stepIcon2">📧</div>
                        <div className="stepContent">
                          <h4>
                            <Translate textKey="share_details" />
                          </h4>
                          <p>
                            <Translate textKey="send_gift_details" />
                          </p>
                        </div>
                      </div>

                      <div className="giftStep">
                        <div className="stepIcon stepIcon3">🎁</div>
                        <div className="stepContent">
                          <h4>
                            <Translate textKey="recipient_takes_over" />
                          </h4>
                          <p>
                            <Translate textKey="they_use_code_to_unlock" />
                          </p>
                        </div>
                      </div>
                      </div>
                    </div>
        )}
        </div>
      </div>
    </>
  );
};

export default GiftAdd;
